/**
 * Robust pagination view for a table footer.
 *
 * The DataCentral customer-scoped list endpoints can report a `total` /
 * `total_pages` larger than the rows they actually return (e.g. a customer's
 * Services `total` counts more than the Official list yields), which would let
 * the user page into an empty "next" page. We rely on the standard REST
 * invariant instead: a page returning fewer rows than requested IS the last
 * page. So we cap the page count and correct the displayed total from what is
 * actually shown.
 *
 * Returns `{ pageCount, summary }` — feed straight into `<Pagination>`.
 */
export function paginationView(
  page: number,
  pageSize: number,
  shown: number,
  total: number,
  pageCount: number,
): { pageCount: number; summary: string } {
  const isLastPage = shown < pageSize; // full page ⇒ maybe more; partial ⇒ last
  const effTotal = isLastPage ? (page - 1) * pageSize + shown : Math.max(total, page * pageSize);
  const effPageCount = isLastPage ? page : Math.max(pageCount || 1, page + 1);

  const from = shown === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = shown === 0 ? 0 : from + shown - 1;
  const summary = effTotal === 0 ? 'Showing 0 of 0' : `Showing ${from}–${to} of ${effTotal}`;

  return { pageCount: Math.max(1, effPageCount), summary };
}
